/**
 * Module dependencies.
 */

import React, { forwardRef, useRef } from 'react';
import { Select as AntdSelect, SelectProps as AntdSelectProps, Spin } from 'antd';
import { FormField } from '../../form-field/form-field';
import { ErrorField } from '../../error-field/error-field';
import styles from './select.module.less';
import classnames from 'classnames';
import { omit } from 'lodash';

/**
 * Export `PaginatedSelectProps` type.
 */

export type PaginatedSelectProps = AntdSelectProps & {
  error?: any;
  className?: string;
  fieldClassName?: string;
  invalid?: boolean;
  isDirty?: boolean;
  label?: string;
  isTouched?: boolean;
  name: string;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
};

/**
 * `PaginatedSelectComponent` component.
 */

function PaginatedSelectComponent(props: PaginatedSelectProps, forwardedRef: any): JSX.Element {
  const { invalid, isTouched, fieldClassName, className, label, onLoadMore, hasNextPage, isFetchingNextPage, ...rest } =
    props;
  const { error } = rest;
  const scrollDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Check if user scrolled to bottom (with 50px threshold)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      // Debounce the load more call
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }

      scrollDebounceRef.current = setTimeout(() => {
        if (hasNextPage && !isFetchingNextPage && onLoadMore) {
          onLoadMore();
        }
      }, 200);
    }
  };

  return (
    <FormField className={className} label={label}>
      <AntdSelect
        className={classnames(styles.wrapper, fieldClassName)}
        dropdownRender={menu => (
          <>
            {menu}
            {isFetchingNextPage && (
              <div style={{ textAlign: 'center', padding: '8px' }}>
                <Spin size={'small'} />
              </div>
            )}
          </>
        )}
        onPopupScroll={handlePopupScroll}
        ref={forwardedRef}
        {...omit(rest, ['isDirty'])}
      />

      <ErrorField error={error} isVisible={invalid || isTouched} />
    </FormField>
  );
}

/**
 * Export `PaginatedSelect` component.
 */

export const PaginatedSelect = forwardRef<any, PaginatedSelectProps>(PaginatedSelectComponent);
